// src/api/axios.ts
import axios, { AxiosError, AxiosResponse } from 'axios';
import type {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  Business,
  BusinessUpdateData,
  Employee,
  Timesheet,
  TimesheetSubmitPayload,
  Subscription,
  SubscriptionCreateUpdate,
  PaymentSummary,
  PaymentSettings,
  TaxPdfPayload,
  DashboardData,
  DateRangeReportRequest,
  DateRangeReport,
  MultiYearComparison,
  MonthlyBreakdown,
  TeamMember,
  InviteUserRequest,
  AcceptInviteRequest,
  TeamActionResponse,
  InviteDetails,
  UpdateTeamMemberRequest,
  TeamMemberLoginRequest,
  AuditLogQueryRequest,
  AuditLogResponse,
  Form1099Record,
  Issue1099Request,
  Correct1099Request,
  Void1099Request,
  Form1099HistoryResponse,
} from '../types/api';

axios.defaults.baseURL =
  process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

axios.defaults.withCredentials = true;

const sleep = () => new Promise((resolve) => setTimeout(resolve, 500));

const responseBody = (response: AxiosResponse) => response.data;

axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

axios.interceptors.response.use(
  async (response) => {
    if (process.env.DEV) await sleep();
    return response;
  },
  (error: AxiosError) => {
    const { status } = (error.response || {}) as AxiosResponse;

    switch (status) {
      case 401:
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        // Session expired - components will handle the error message
        break;
      case 404:
        console.error('Not Found');
        break;
      case 500:
        console.error('Server Error');
        break;
      default:
        console.error('Unexpected Error');
        break;
    }

    return Promise.reject(error);
  }
);

const requests = {
  get: <T = any>(url: string, params?: Record<string, any>) =>
    axios.get<T>(url, { params }).then(responseBody),
  post: <T = any>(url: string, body: object) =>
    axios.post<T>(url, body).then(responseBody),
  put: <T = any>(url: string, body: object) =>
    axios.put<T>(url, body).then(responseBody),
  del: <T = any>(url: string) => axios.delete<T>(url).then(responseBody),
  postFile: (url: string, body: {}) =>
    axios.post(url, body, { responseType: 'blob' }).then(responseBody),
};

const Auth = {
  register: (values: RegisterData) =>
    requests.post<AuthResponse>('auth/register', values),
  login: (values: LoginCredentials) =>
    requests.post<AuthResponse>('auth/login', values),
  currentUser: () => requests.get<Business>('auth/me'),
  impersonate: (businessId: string) =>
    requests.post<AuthResponse>('auth/impersonate', { businessId }),
  forgotPassword: (email: string) =>
    requests.post<{ message: string }>('auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    requests.post<{ message: string }>('auth/reset-password', {
      token,
      newPassword,
    }),
};

const BusinessAPI = {
  register: (registerData: RegisterData) =>
    requests.post<{ businessId: string; sessionId: string }>(
      'business/register',
      registerData
    ),
  confirmPayment: (payload: {
    businessId: string;
    subscriptionId: string;
    paymentMethodId: string;
  }) => requests.post<{ message: string }>('business/confirm-payment', payload),
  getStatus: (businessId: string) =>
    requests.get<{ status: string; business?: Business }>(
      `business/status/${businessId}`
    ),
  getSession: (sessionId: string) =>
    requests.get<{ clientSecret: string }>(`business/session/${sessionId}`),
  get: () => requests.get<Business[]>('business'),
  getAll: () => requests.get<Business[]>('business/all'),
  getById: (id: string) => requests.get<Business>(`business/${id}`),
  update: (business: BusinessUpdateData) =>
    requests.put<void>('business', business),
  toggleActive: (id: string, isActive: boolean) =>
    requests.put<void>(`business/${id}/toggle-active`, { isActive }),
  delete: (id: string) => requests.del<void>(`business/${id}`),
  createAdmin: (data: { name: string; ownerEmail: string; password: string }) =>
    requests.post<{ businessId: string }>('business/create-admin', data),
  getMySubscription: () =>
    requests.get<{ subscription: Subscription; currentPeriodEnd: string }>(
      'business/my-subscription'
    ),
  cancelSubscription: () =>
    requests.post<{ message: string }>('business/cancel-subscription', {}),
  reactivateSubscription: () =>
    requests.post<{ message: string }>('business/reactivate-subscription', {}),
  previewSubscriptionChange: (newSubscriptionId: string) =>
    requests.post<{
      proration: number;
      newAmount: number;
      isScheduledDowngrade?: boolean;
      scheduledChangeDate?: string;
      message?: string;
    }>('business/preview-subscription-change', {
      newSubscriptionId,
    }),
  changeSubscription: (newSubscriptionId: string) =>
    requests.post<{
      message: string;
      isScheduledDowngrade?: boolean;
      scheduledChangeDate?: string;
    }>('business/change-subscription', {
      newSubscriptionId,
    }),
  cancelScheduledDowngrade: () =>
    requests.post<{ message: string }>(
      'business/cancel-scheduled-downgrade',
      {}
    ),
  getCurrentPayPeriod: () =>
    requests.get<{
      payFrequency: string;
      endOfWeek: number;
      endOfWeekName: string;
      payCheckDelay: number;
      currentPeriod: {
        start: string;
        end: string;
        payCheckDate: string;
      };
      previousPeriod: {
        end: string;
        payCheckDate: string;
      };
      nextPeriod: {
        end: string;
        payCheckDate: string;
      };
    }>('business/current-pay-period'),
  uploadLogo: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios
      .post<{ logoUrl: string; logoFileName: string }>(
        'business/logo',
        formData
      )
      .then((res) => res.data);
  },
  deleteLogo: () => requests.del<{ message: string }>('business/logo'),
};

const Employees = {
  list: () => requests.get<Employee[]>('employees'),
  add: (employee: Employee) => requests.post<Employee>('employees', employee),
  update: (id: string, employee: Employee) =>
    requests.put<void>(`employees/${id}`, employee),
  remove: (id: string) => requests.del<void>(`employees/${id}`),
};

const Tax = {
  getForYear: (year: number) => requests.get<PaymentSummary[]>(`tax/${year}`),
  download1099PDF: (data: TaxPdfPayload) =>
    requests.postFile(`/Tax/CreatePDF1099`, data),
  downloadEmployer1099PDF: (data: TaxPdfPayload) =>
    requests.postFile(`/Tax/CreateEmployerPDF1099`, data),
  getTaxYears: () => requests.get<number[]>('tax/years'),
};

const Export = {
  downloadPaymentsCsv: (year: number) =>
    axios
      .get(`export/payments/${year}`, { responseType: 'blob' })
      .then((res) => res.data),
  downloadContractorsCsv: () =>
    axios
      .get('export/contractors', { responseType: 'blob' })
      .then((res) => res.data),
  downloadTimesheetsCsv: (employeeId: string, year?: number) =>
    axios
      .get(`export/timesheets/${employeeId}`, {
        params: year ? { year } : undefined,
        responseType: 'blob',
      })
      .then((res) => res.data),
};

// Payments API (list years, paged payments by year, etc.)
const Payments = {
  // get available tax years (backend should return number[] or similar)
  getTaxYear: () => requests.get('payments/years'),
  // get payments by year with optional server-side paging/sorting/search
  getAllPaymentsByYear: (
    year: string,
    page?: number,
    pageSize?: number,
    orderBy?: string,
    order?: 'asc' | 'desc',
    searchTerm?: string
  ) =>
    requests.get('payments', {
      year,
      page,
      pageSize,
      orderBy,
      order,
      search: searchTerm,
    }),
  // fallback to download all payments CSV/data for given year
  exportPaymentsByYear: (year: string) =>
    requests.get('payments/export', { year }),
};

const Options = {
  // tax PDF visibility option per year
  getTaxPdfOptionByYear: (year: string) =>
    requests.get(`options/taxpdf/${year}`),
  updateTaxPdfOption: (payload: { year: number; available: boolean }) =>
    requests.put('options/taxpdf', payload),
};

const Timesheets = {
  submit: (payload: TimesheetSubmitPayload) =>
    requests.post<void>('timesheets', payload),
  list: (payeeId: string) => requests.get<Timesheet[]>(`timesheets/${payeeId}`),
  getByWeekEnding: (payeeId: string, weekEnding: string) =>
    requests.get<Timesheet>(`timesheets/${payeeId}/${weekEnding}`),
};

const Subscriptions = {
  get: () => requests.get<Subscription[]>('subscriptions'),
  getById: (id: string) => requests.get<Subscription>(`subscriptions/${id}`),
  create: (payload: SubscriptionCreateUpdate) =>
    requests.post<Subscription>('subscriptions', payload),
  update: (id: string, payload: SubscriptionCreateUpdate) =>
    requests.put<void>(`subscriptions/${id}`, payload),
  delete: (id: string) => requests.del<void>(`subscriptions/${id}`),
};

const PaymentSettingsAPI = {
  get: () => requests.get<PaymentSettings>('paymentsettings'),
  update: (payload: PaymentSettings) =>
    requests.put<void>('paymentsettings', payload),
  getEnabledMethods: () =>
    requests.get<string[]>('paymentsettings/enabled-methods'),
};

const Dashboard = {
  get: () => requests.get<DashboardData>('dashboard'),
};

const Reports = {
  getDateRangeReport: (request: DateRangeReportRequest) =>
    requests.post<DateRangeReport>('reports/date-range', request),
  getMultiYearComparison: (startYear: number, endYear: number) =>
    requests.get<MultiYearComparison>('reports/compare-years', {
      startYear,
      endYear,
    }),
  getMonthlyBreakdown: (year: number) =>
    requests.get<MonthlyBreakdown>(`reports/monthly/${year}`),
  getAvailableYears: () => requests.get<number[]>('reports/available-years'),
  exportCsv: (request: DateRangeReportRequest) =>
    axios
      .post('reports/export-csv', request, { responseType: 'blob' })
      .then((res) => res.data),
};

const Team = {
  list: () => requests.get<TeamMember[]>('team'),
  invite: (request: InviteUserRequest) =>
    requests.post<TeamActionResponse>('team/invite', request),
  acceptInvite: (request: AcceptInviteRequest) =>
    requests.post<AuthResponse>('team/accept-invite', request),
  getInviteDetails: (token: string) =>
    requests.get<InviteDetails>(`team/invite/${token}`),
  update: (id: string, request: UpdateTeamMemberRequest) =>
    requests.put<void>(`team/${id}`, request),
  remove: (id: string) => requests.del<void>(`team/${id}`),
  resendInvite: (id: string) =>
    requests.post<TeamActionResponse>(`team/${id}/resend-invite`, {}),
  login: (request: TeamMemberLoginRequest) =>
    requests.post<AuthResponse>('team/login', request),
};

const AuditLogs = {
  list: (query?: AuditLogQueryRequest) => {
    // Map client query params to API params
    const apiParams: Record<string, any> = {};
    if (query?.action) apiParams.action = query.action;
    if (query?.startDate) apiParams.startDate = query.startDate;
    if (query?.endDate) apiParams.endDate = query.endDate;
    if (query?.page) apiParams.pageNumber = query.page;
    if (query?.pageSize) apiParams.pageSize = query.pageSize;
    return requests.get<AuditLogResponse>('audit', apiParams);
  },
  getActions: () => requests.get<string[]>('audit/actions'),
};

const Form1099 = {
  list: (year?: number) =>
    requests.get<Form1099Record[]>('form1099', year ? { year } : undefined),
  getEmployeeHistory: (employeeId: string, year?: number) =>
    requests.get<Form1099HistoryResponse>(
      `form1099/employee/${employeeId}`,
      year ? { year } : undefined
    ),
  issue: (request: Issue1099Request) =>
    requests.post<Form1099Record>('form1099/issue', request),
  correct: (request: Correct1099Request) =>
    requests.post<Form1099Record>('form1099/correct', request),
  void: (request: Void1099Request) =>
    requests.post<Form1099Record>('form1099/void', request),
  download: (formId: string) =>
    axios
      .get(`form1099/${formId}/download`, { responseType: 'blob' })
      .then((res) => res.data),
  markMailed: (formId: string) =>
    requests.post<Form1099Record>(`form1099/${formId}/mark-mailed`, {}),
};

const agent = {
  Auth,
  Employees,
  Tax,
  Export,
  Dashboard,
  Reports,
  Team,
  AuditLogs,
  Form1099,
  Business: BusinessAPI,
  Timesheets,
  Payments,
  Options,
  Subscriptions,
  PaymentSettings: PaymentSettingsAPI,
};

export default agent;
